import { useCallback, useEffect, useState } from "react"
import { ApiClient } from "../../ApiClient"
import { useAuth0 } from "@auth0/auth0-react"
import { useParams } from "react-router-dom"
import { MessageType, useMessageReport } from "../../components/MessageReporter"
import { RemoteLogging } from "../../components/Device/RemoteLogging"
import { DeviceTree } from "../../components/Device/DeviceTree"
import { FirmwareChain } from "../../components/Firmware/FirmwareChain"
import { DeviceInfo } from "../../components/Device/DeviceInfo"
import { DeviceInfoAdmin } from "../../components/Device/DeviceInfoAdmin"
import { DeviceLatestValues } from "../../components/Device/DeviceLatestValues"
import { Col, Row, Button, Table, Collapse } from "react-bootstrap"
import { RoleProtect } from "../../components/Authorization/RoleProtect"
import { SystemGraphs } from "../../components/Device/SystemGraphs"
import { formatDateTime } from "../../util/formatDataTime"
import { Permission } from "../../api"

const devicesWithoutLatestValues = ["BEN"]

const deprecatedSettingValueMap = {
    "All-Electric": {
        0: "No",
        1: "Yes",
    },
    "Thermostat type": {
        0: "No thermostat",
        1: "BEN thermostat (JOC)",
        2: "OpenTherm",
        3: "On-Off",
        4: "Multi-Zoning",
    },
    "Duct bend configuration": {
        0: "Straight (first 0.5 meters)",
        1: "Yes, a bend to the left",
        2: "Yes, a bend to the right",
    },
    "Backup Heater power": {
        0.0: "0 kW",
        1.5: "1.5 kW (min. 1Phase 32A/2Phase 16A required)",
        3.0: "3.0 kW (min. 1Phase 32A/2Phase 16A required)",
        4.5: "4.5 kW (min. 3Phase 16A required)",
        6.0: "6.0 kW (min. 3Phase 16A required)",
    },
    "Outdoor temperature sensor": {
        0: "Indoor temperature sensor",
        1: "Outdoor temperature sensor",
    },
    "Air source": {
        0: "Ventilation air only",
        1: "Outdoor air only",
        2: "Ventilation and outdoor air",
    },
    "DHW tank type": {
        0: "No DHW tank available",
        1: "BEN tank",
        2: "Other tank",
    },
    "Legionella day": {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
    },
    "Heating curve": {
        0: "Predefined",
    },
    "Heating system type": {
        0: "Low temperature (underfloor heating)",
        1: "Medium temperature (radiators)",
        2: "High temperature (convector)",
    },
    "DHW heating mode": {
        0: "Eco",
        1: "Comfort",
    },
}

const settingValueMap = {
    is_all_electric: {
        false: "No",
        true: "Yes",
    },
    backup_heater_power_kw: {
        0.0: "0 kW",
        1.5: "1.5 kW (min. 1Phase 32A/2Phase 16A required)",
        3.0: "3.0 kW (min. 1Phase 32A/2Phase 16A required)",
        4.5: "4.5 kW (min. 3Phase 16A required)",
        6.0: "6.0 kW (min. 3Phase 16A required)",
    },
    is_external_outdoor_temperature_sensor: {
        false: "Indoor temperature sensor",
        true: "Outdoor temperature sensor",
    },
    cooling_is_available: {
        false: "No",
        true: "Yes",
    },
}

const settingMappers = {
    legionella: (setting) => {
        return setting.time.toString() + " on " + setting.week_day
    },
    dhw_preferences: (setting) => {
        const start_date = new Date(setting.allowed_start_time)
        const end_date = new Date(setting.allowed_end_time)
        return (
            "between " +
            start_date.toISOString().slice(11, 16) +
            " and " +
            end_date.toISOString().slice(11, 16) +
            " in mode " +
            setting.mode
        )
    },
}

const splitOnCaps = (str) => {
    return str.replace(/([A-Z])/g, " $1").trim()
}

const convertSettingValue = (key, value) => {
    const mapped = settingValueMap[key]?.[value]
    if (mapped) {
        return mapped
    }
    const mapper = settingMappers[key]
    if (mapper) {
        return mapper(value)
    }

    if (typeof value === "string") {
        return splitOnCaps(value)
    }

    return value
}

export const DeviceDetail = () => {
    const { getAccessTokenSilently } = useAuth0()
    const { addMessage } = useMessageReport()

    const { id } = useParams()
    const [device, setDevice] = useState()
    const [gaveConsent, setGaveConsent] = useState(false)
    const [deviceHasBeenReset, setDeviceHasBeenReset] = useState(false)
    const [report, setReport] = useState({ date: null, settings: [] })
    const [showSettings, setShowSettings] = useState(false)

    const loadDevice = useCallback(() => {
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.get("/v2/management/devices/" + id, {
                    Authorization: "Bearer " + token,
                })
            )
            .then(({ data }) => {
                setDevice(data)
            })
            .catch((e) => {
                addMessage(
                    "Failed to get device",
                    "Something went wrong while getting the device, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }, [getAccessTokenSilently, addMessage, id])

    const reload = () => loadDevice()
    useEffect(() => loadDevice(), [loadDevice])

    useEffect(() => {
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.get(`/v2/consumer/devices/${id}/config?filter=sharing_policy`, {
                    Authorization: "Bearer " + token,
                })
            )
            .then(({ data }) => {
                setGaveConsent(data?.sharing_policy?.shared === true)
            })
            .catch((e) => {
                addMessage(
                    "Failed to get user consent",
                    "Something went wrong while getting the users consent information, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }, [getAccessTokenSilently, addMessage, id])

    const loadSettings = useCallback(async () => {
        const fetchSettings = async (token) => {
            const { data } = await ApiClient.get(
                `/v2/consumer/devices/${id}/config?filter=settings`,
                {
                    Authorization: "Bearer " + token,
                }
            )

            if (Object.keys(data.settings).length <= 1) {
                // Reported At is always present but there should be more to be considered valid.
                throw Error("no settings received")
            }
            setReport({
                date: data.settings.reported_at,
                settings: Object.entries(data.settings)
                    .filter(([key]) => key !== "reported_at")
                    .map(([key, value]) => ({
                        key: key.replaceAll("_", " "),
                        value: convertSettingValue(key, value),
                    })),
            })
        }

        const fetchDeprecatedSettings = async (token) => {
            const { data } = await ApiClient.get(`/v2/technician/devices/${device.sn}/settings`, {
                Authorization: "Bearer " + token,
            })
            setReport({
                date: data.reported_at,
                settings: data.settings.map((s) => ({
                    ...s,
                    value: deprecatedSettingValueMap[s.key]?.[s.value] ?? s.value,
                })),
            })
        }

        const token = await getAccessTokenSilently()
        try {
            await fetchSettings(token)
        } catch {
            try {
                await fetchDeprecatedSettings(token)
            } catch (e) {
                addMessage(
                    "Failed to get device settings",
                    "Something went wrong while getting the device settings, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            }
        }
    }, [getAccessTokenSilently, addMessage, setReport, device, id])

    useEffect(() => {
        if (!device?.sn) {
            return
        }

        loadSettings()
    }, [device, loadSettings])

    const resetDevice = () => {
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.patch(
                    `/v2/consumer/devices/${id}/config?filter=device_reset`,
                    {
                        device_reset: {},
                    },
                    {
                        Authorization: "Bearer " + token,
                    }
                )
            )
            .then(() => {
                setDeviceHasBeenReset(true)
            })
            .catch((e) => {
                addMessage(
                    "Failed to reset device",
                    "Something went wrong while resetting the device, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }

    return (
        <>
            <Row className="content-section">
                <Col>
                    <h2>Information</h2>
                    {device && <DeviceInfo device={device} />}
                </Col>
                {device &&
                    device.connected &&
                    gaveConsent &&
                    !devicesWithoutLatestValues.includes(device.type) && (
                        <Col md={6}>
                            <h2>Status</h2>
                            <DeviceLatestValues id={id} />
                        </Col>
                    )}
            </Row>

            {report.settings.length > 0 && (
                <Row className="content-section">
                    <Col>
                        <h2>Settings</h2>
                        <p>
                            The settings from the latest provisioning report, not necessarily the
                            currently active settings.
                        </p>
                        <p>This report was sent on: {formatDateTime(report.date)}</p>
                        <Button onClick={() => setShowSettings(!showSettings)}>
                            {showSettings ? "Hide" : "Show"} settings
                        </Button>
                        <Collapse in={showSettings} className="mt-3">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.settings.map((setting) => (
                                        <tr key={setting.key}>
                                            <td>{setting.key}</td>
                                            <td>{setting.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Collapse>
                    </Col>
                </Row>
            )}

            <RoleProtect
                allowedPermissions={[
                    Permission.SetAnyResourceConfDeviceReset,
                    Permission.SetIndirectLinkConfDeviceReset,
                ]}
            >
                <Row className="content-section">
                    <Col>
                        <h2>Device Reset</h2>
                        <p>Command the device to reset itself. Use with caution.</p>
                        {device && device.device_id && (
                            <Button variant="success" onClick={resetDevice}>
                                Reset device
                            </Button>
                        )}
                        {deviceHasBeenReset && (
                            <div className="text-success">Device has been reset.</div>
                        )}
                    </Col>
                </Row>
            </RoleProtect>

            <RoleProtect
                allowedPermissions={[
                    Permission.SetAnyResourceFirmware,
                    Permission.SetIndirectLinkFirmware,
                    Permission.SetDirectLinkFirmware,
                ]}
            >
                <Row className="content-section">
                    <Col xs={12}>
                        <h2 className="heading-small mb-4">Administration</h2>
                    </Col>

                    <Col lg={6}>
                        <h4 className="mb-4">Information</h4>
                        {device && <DeviceInfoAdmin device={device} reloadDevice={reload} />}
                        <hr className="d-lg-none" />
                    </Col>
                    <Col lg={6}>
                        <h4>Device groups & firmware version</h4>
                        {device && (
                            <FirmwareChain
                                firmware={device.set_firmware}
                                doneLoading={device.device_id !== undefined}
                            />
                        )}
                        <hr />
                        <h4>Attached devices</h4>
                        {device && <DeviceTree device={device} />}
                    </Col>

                    <Col xs={12}>
                        <hr />
                    </Col>

                    <Col md={6}>
                        <h5>Remote logging</h5>
                        {device && device.device_id && (
                            <RemoteLogging deviceId={device.device_id} />
                        )}
                    </Col>
                </Row>
            </RoleProtect>

            <Row className="content-section">
                <Col>
                    <h2>History</h2>
                    {device && (
                        <SystemGraphs
                            sn={device.sn}
                            id={device.device_id}
                            isDevice={true}
                            includeAnalytics={false}
                        />
                    )}
                </Col>
            </Row>
        </>
    )
}
